// src/components/admin/payroll/PayrollHub.js
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../hooks/useAuth';
import {
    BarChart3, DollarSign, Shield, RefreshCw
} from 'lucide-react';

// Lazy load tab components for better performance
const PayrollAnalytics = lazy(() => import('./PayrollAnalytics'));
const PayrollRun = lazy(() => import('./PayrollRun'));
const PayrollAuditTrail = lazy(() => import('../PayrollAuditTrail'));

// Loading fallback component
const TabLoadingFallback = () => {
    const { theme } = useTheme();
    return (
        <div className={`flex items-center justify-center h-64 ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
            <div className="flex items-center space-x-3">
                <RefreshCw className={`w-6 h-6 animate-spin ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    Loading...
                </span>
            </div>
        </div>
    );
};

const PayrollHub = () => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const isSuperAdmin = user?.role_name === 'Super Admin';

    // Tab configuration
    const tabs = [
        {
            id: 'analytics',
            label: 'Analytics',
            icon: BarChart3,
            description: 'Payroll insights and metrics',
            show: true
        },
        {
            id: 'run',
            label: 'Payroll Run',
            icon: DollarSign,
            description: 'Process monthly payroll',
            show: true
        },
        {
            id: 'audit',
            label: 'Audit Trail',
            icon: Shield,
            description: 'Payroll activity logs',
            show: isSuperAdmin // Only show for Super Admin
        }
    ].filter(tab => tab.show);

    // Get initial tab from URL query parameter
    const getInitialTab = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        const validTabIds = tabs.map(t => t.id);
        return validTabIds.includes(tabParam) ? tabParam : 'analytics';
    }, [location.search, tabs]);

    const [activeTab, setActiveTab] = useState(getInitialTab());

    // Update URL when tab changes
    const handleTabChange = useCallback((tabId) => {
        setActiveTab(tabId);
        const params = new URLSearchParams(location.search);
        params.set('tab', tabId);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }, [location.pathname, location.search, navigate]);

    // Sync tab with URL on mount and URL changes
    useEffect(() => {
        // Only run this effect if we're still on the payrollrun route
        // This prevents interference when navigating away
        if (!location.pathname.includes('/admin/payrollrun')) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        const validTabIds = tabs.map(t => t.id);

        if (tabParam && validTabIds.includes(tabParam)) {
            setActiveTab(tabParam);
        } else if (!tabParam) {
            // If no tab in URL, set default
            const params = new URLSearchParams();
            params.set('tab', 'analytics');
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        }
    }, [location.search, location.pathname, navigate, tabs]);

    // Render active tab content
    const renderTabContent = () => {
        switch (activeTab) {
            case 'analytics':
                return (
                    <Suspense fallback={<TabLoadingFallback />}>
                        <PayrollAnalytics />
                    </Suspense>
                );
            case 'run':
                return (
                    <Suspense fallback={<TabLoadingFallback />}>
                        <PayrollRun />
                    </Suspense>
                );
            case 'audit':
                if (!isSuperAdmin) {
                    return (
                        <div className={`p-8 text-center ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Access denied. Super Admin privileges required.
                        </div>
                    );
                }
                return (
                    <Suspense fallback={<TabLoadingFallback />}>
                        <PayrollAuditTrail embedded={true} />
                    </Suspense>
                );
            default:
                return null;
        }
    };

    return (
        <div className={`h-full flex flex-col ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`}>
            {/* Tab Header */}
            <div className={`border-b ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="px-6 pt-4">
                    {/* Title */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <DollarSign className={`w-7 h-7 ${
                                theme === 'dark' ? 'text-green-400' : 'text-green-600'
                            }`} />
                            <div>
                                <h1 className={`text-xl font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>Payroll</h1>
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Manage payroll processing, analytics, and audit trails
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex space-x-1">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`tour-payrollhub-tab-${tab.id} flex items-center space-x-2 px-4 py-3 font-medium text-sm rounded-t-lg transition-all duration-200 ${
                                        isActive
                                            ? theme === 'dark'
                                                ? 'bg-gray-900 text-blue-400 border-b-2 border-blue-400'
                                                : 'bg-gray-100 text-blue-600 border-b-2 border-blue-600'
                                            : theme === 'dark'
                                                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                    title={tab.description}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-auto">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default PayrollHub;
